import {useCallback, useEffect, useMemo, useState} from 'react';
import {isEmpty} from 'lodash';
import {useTranslation} from 'react-i18next';

import BorderedTextSeparator from '@/components/bordered-text-separator';
import Dialog from '@/components/dialog';
import {DocumentCreationActionsContextValue} from '@/components/new-document-creation/dialog/context/document-create-actions.context';
import {DocumentCreationActionsProvider} from '@/components/new-document-creation/dialog/context/document-create-actions.provider';
import {CreateForNewContractHolderStep} from '@/components/new-document-creation/dialog/create-for-new-contract-holder-step';
import {CreateForRecentContractHolderStep} from '@/components/new-document-creation/dialog/create-for-recent-contract-holder-step';
import {SearchContractHolderStep} from '@/components/new-document-creation/dialog/search-contract-holder-step';
import {InvestorComboboxValue} from '@/components/new-document-creation/dialog/search-investors-as-combobox-values';
import {NewDocumentCreationDialogAction} from '@/components/new-document-creation/hooks/use-create-document-mutation';
import {useGetDetailedInvestorAccountMutation} from '@/components/new-document-creation/hooks/use-get-investor-account-mutation';
import {useUserProfileContext} from '@/contexts/user-profile';
import {useWebViewContentOnDialogAppear} from '@/hooks/analytics/use-web-view-content-on-dialog-appear';
import {Toaster} from '@/initializers/toaster';
import {LatestTransaction} from '@/repositories/beneva-api/endpoints/documents';

import {AccountStep} from './account-step';
import {AdvisorCodes, AdvisorCodeStep} from './advisor-code-step';

type Props = {
  latestTransaction: LatestTransaction | undefined;
  isVisible: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: (action: NewDocumentCreationDialogAction) => void;
};

const NewDocumentCreationDialog = ({latestTransaction, isVisible, isLoading, onConfirm, onClose}: Props) => {
  const {t} = useTranslation();
  const {profile} = useUserProfileContext();

  const [advisorCodes, setAdvisorCodes] = useState<AdvisorCodes | null>(null);

  const [selectedInvestor, setSelectedInvestor] = useState<InvestorComboboxValue | null>(null);

  const recentContractHolders = latestTransaction?.clients || [];
  const stepAdvisorCodeCompleted = advisorCodes?.repCode && advisorCodes?.dealerCode;
  const stepContractHolderCompleted = selectedInvestor !== null;

  const advisorCode = useMemo(() => {
    if (!advisorCodes) {
      return null;
    }
    return `${advisorCodes.dealerCode}-${advisorCodes.repCode}`;
  }, [advisorCodes]);

  const onCreateForNewContractHolder = useCallback<
    DocumentCreationActionsContextValue['onCreateForNewContractHolder']
  >(() => {
    if (!advisorCode) {
      return;
    }
    onConfirm({type: 'new', advisorCode});
  }, [advisorCode, onConfirm]);

  const onCreateForRecentContractHolder = useCallback<
    DocumentCreationActionsContextValue['onCreateForRecentContractHolder']
  >(
    contractHolder => {
      if (!latestTransaction || !advisorCode) {
        return;
      }
      onConfirm({type: 'recent', latestTransaction, advisorCode, contractHolder});
    },
    [latestTransaction, advisorCode, onConfirm],
  );

  const onCreateForNewAccount = useCallback<DocumentCreationActionsContextValue['onCreateForNewAccount']>(
    investor => {
      if (!advisorCode) {
        return;
      }
      onConfirm({type: 'newAccount', advisorCode, investor});
    },
    [advisorCode, onConfirm],
  );

  const {mutate: mutateGetInvestorAccount, isPending: isPendingGetInvestorAccount} =
    useGetDetailedInvestorAccountMutation();

  const onCreateForExistingAccount = useCallback<DocumentCreationActionsContextValue['onCreateForExistingAccount']>(
    (investor, accountNo) => {
      mutateGetInvestorAccount(
        {investorNo: investor.investorNo.toString(), accountNo: accountNo.toString()},
        {
          onError: () => {
            Toaster.error(
              t('components.new-document-creation.dialog.content.step-account.existing-accounts.create-error'),
            );
          },
          onSuccess: account => {
            const advisorCode = `${account.dealerCode}-${account.repCode}`;
            onConfirm({type: 'existingAccount', advisorCode, investor, account});
          },
        },
      );
    },
    [mutateGetInvestorAccount, onConfirm, t],
  );

  const documentCreationActions = useMemo(
    () => ({
      onCreateForNewContractHolder,
      onCreateForRecentContractHolder,
      onCreateForNewAccount,
      onCreateForExistingAccount,
    }),
    [onCreateForNewContractHolder, onCreateForRecentContractHolder, onCreateForNewAccount, onCreateForExistingAccount],
  );

  useResetContractHolderStepOnAdvisorCodesChange(advisorCodes, setSelectedInvestor);

  useWebViewContentOnDialogAppear('modale-nouvelle-demande', null, isVisible);

  const resetDialogState = useCallback(() => {
    if (!isVisible) {
      setAdvisorCodes(null);
      setSelectedInvestor(null);
    }
  }, [isVisible]);

  return (
    <Dialog
      isVisible={isVisible}
      isLoading={isLoading || isPendingGetInvestorAccount}
      onClose={onClose}
      onCloseTransitionEnded={resetDialogState}
    >
      <Dialog.Header onClose={onClose} title={t('components.new-document-creation.dialog.title')} />

      <Dialog.Content className="relative flex flex-col gap-4" data-testid="new-document-creation-dialog-content">
        <DocumentCreationActionsProvider value={documentCreationActions}>
          <AdvisorCodeStep profile={profile} onChange={setAdvisorCodes} />

          {stepAdvisorCodeCompleted && (
            <>
              <BorderedTextSeparator />

              <SearchContractHolderStep
                advisorCodes={advisorCodes}
                selectedContractHolder={selectedInvestor}
                onSelectContractHolder={setSelectedInvestor}
              />

              {!stepContractHolderCompleted ? (
                <>
                  <CreateForNewContractHolderStep />
                  {!isEmpty(recentContractHolders) && (
                    <CreateForRecentContractHolderStep contractHolders={recentContractHolders} />
                  )}
                </>
              ) : (
                <>
                  <BorderedTextSeparator />
                  <AccountStep investorNo={selectedInvestor.id} />
                </>
              )}
            </>
          )}
        </DocumentCreationActionsProvider>
      </Dialog.Content>
    </Dialog>
  );
};

const useResetContractHolderStepOnAdvisorCodesChange = (
  advisorCodes: AdvisorCodes | null,
  setSelectedInvestor: (value: InvestorComboboxValue | null) => void,
) => {
  useEffect(() => {
    setSelectedInvestor(null);
  }, [advisorCodes, setSelectedInvestor]);
};

export default NewDocumentCreationDialog;
